import { GuestBasics } from '../../guest/guestBasics';
import { Household } from '../../guestSearch/household/householdMember';
import { GuestDetails } from '../../guest/guestDetails';
import { ApiError } from './api/apiError';
import { ApiLoading } from './api/apiLoading';
import { ChipIdSet } from '../../guest/entitlements/media/passPrinting/chipId';
import { Redemption } from '../../guest/entitlements/redemption/redemption';
import { RedemptionType } from '../../guest/entitlements/redemption/redemptionType';
import { CircuitBreaker } from './api/circuitBreaker/circuitBreaker';
import { HistoricalMedia } from '../../guest/entitlements/media/mediaHistory/historicalMedia';
import { GuestComment } from '../../guest/guestComments/guestComment';
import { HotlistReason } from '../../guest/hotlist/hotlistReason';
import { Resort } from '../../resortDetails/resort';
import { DEFAULT_CONFIG } from '../../auth/config/defaultConfig';
import { AuthState } from '../../auth/authState';
import { User } from '../../admin/userManagement/user';
import ImmutableMap from '../../language/ImmutableMap';
import { Snackbar } from '../../snackbar/snackbar';
import { IKON_MODAL } from '../modal/ikonModal';
import { PassPdf } from '../../guest/entitlements/media/passPrinting/passPdf';
import { Entitlement, EntitlementShare, EntitlementHotlist } from '../../guest/entitlements/models';
import { AppDocItem } from '../../appDocs/appDocItem';
import { TrainingDocument } from '../../helpAndTraining/trainingDocument';
import {
  EntitlementRedemptionHistory,
} from '../../guest/entitlements/redemption/viewDays/EntitlementRedemptionHistory';
import { EntitlementType } from '../../entitlementTypes/entitlementType';
import { LiftAccessValidLocationsEntry } from '../../guest/entitlements/liftAccess/liftAccessValidLocations';
import { EntitlementHotlistType as HotlistType } from '../../guest/hotlist/entitlementHotlistType';
import {
  defaultResortMappingObject,
  ResortMappingObject,
} from '../../resortMappingTool/productMappings';
import {UserState} from '../../auth/userState';
import {ResortToolsConfig} from '../../auth/config/resortToolsConfig';
import en_us from '../../language/resources/enResources';
import { BlackoutDateMetadata } from '../../entitlementTypesMetadata/entitlementTypeMetadata';
import { ProductHydrationErrors } from '../../resortMappingTool/hydrationErrors';
import { VersionResponse } from '../../admin/systemAdmin/versionResponse';
import { UserSearchHistory } from '../../guestSearch/userSearchHistory';
import { PaymentGuest } from '../../guest/guestPayment/paymentGuest';
import { EditableEffectiveDateRangeAccessRule, LiftAccessRuleHistory } from '../../validationConfigurationTool/models';
import { GuestHydrationErrorReportData, WeeklyVisitorDataReport } from '../../reports/models';
import { FulfillmentSeason, SellingSeason } from '../../productAdministration/models';
import { LiftAccessRuleSet } from '../../productAdministration/accessRules/liftAccessRuleSet/models';
import { BulkOperationProgress } from '../../forms/elements/BulkProgressCard';



/**
 * Type definition for the app's state in Redux.
 */
export interface IAppState {
  /**
   * All about those Ikon Resorts, like which resort are we at, and what are the other resorts
   */
  readonly resortDetails?: {
    readonly isLoadingResorts: boolean;
    readonly resortLoadError?: string;
    readonly homeResort: Resort;
    readonly allResorts: ImmutableMap<Resort>;
  };

  /**
   * All entitlement types
   */
  readonly entitlementTypesDetails?: {
    readonly isLoading: boolean;
    readonly error?: string;
    readonly allEntitlementTypes?: ImmutableMap<EntitlementType>;
  };

  /**
   * Validation Configuration - Editable Effective Date Range Access Rules
   */
  readonly editableEffectiveDateRangeAccessRules: EditableEffectiveDateRangeAccessRule[];
  readonly effectiveDateRangeHistory: LiftAccessRuleHistory[];

  readonly fnfBlackoutDateMetadata: BlackoutDateMetadata[];
  readonly liftBlackoutDateMetadata: BlackoutDateMetadata[];

  readonly sellingSeasons: SellingSeason[];
  readonly fulfillmentSeasons: FulfillmentSeason[];
  
  readonly liftAccess?: {
    lastFilter?: string;                  // normalized filter used for bailout
    liftAccessRuleSets: LiftAccessRuleSet[];
    /** When the search that produced these landed. Undefined until one has. */
    liftAccessRuleSetsLoadedAt?: number;
  };

  readonly bulkOperationProgress?: BulkOperationProgress;

  readonly waiverLookup?: ImmutableMap<string>;
  readonly selectedResort: Resort;

  readonly auth: AuthState;

  readonly userState: UserState;

  readonly resortToolsConfig: ResortToolsConfig;

  readonly applianceVersion: string;

  // Network
  readonly loading: ImmutableMap<ApiLoading>;
  readonly error: ImmutableMap<ApiError>;

  readonly breakerBox: {
    readonly maxFailureCount: number;
    readonly coolDownTimeMs: number;
    readonly breakers: ImmutableMap<CircuitBreaker>;
  };

  /**
   * When agents add guests to the hotlist (blacklist), they need a reason -- something the guest did wrong. We load the
   * available reasons from the network and put it here.
   */
  readonly hotlistReasons?: {
    isLoading: boolean;
    error?: string;
    reasons?: HotlistReason[];
  };

  readonly entitlementHotlistTypes?: {
    isLoading: boolean;
    error?: string;
    types?: HotlistType[];
  }

  readonly guestSearchResults?: {
    readonly query: string;
    readonly isLoading: boolean;
    readonly results?: GuestBasics[];
    readonly error?: string;
  };

  readonly guests: ImmutableMap<GuestDetails>;
  readonly knownGuestRfidCodes: ChipIdSet;
  readonly households: Household[];
  readonly entitlementHotlists: ImmutableMap<EntitlementHotlist[]>;

  /**
   * Information on Pass usage by Guest Id, Entitlement Id and Resort Abbrev
   * Containing historical details on each day the guest spent on the mountain.
   */
  readonly guestResortHistories: ImmutableMap<Entitlement>;

  /**
   * Information for Resort Mapping tool. Each item in the array is a row in the mapping tool
   */
  readonly resortMappingHistories: ResortMappingObject;

  readonly mappingRequestId: string;

  readonly mappingErrorRequestId: string;

  /** Resort mapping errors have been asked for. Set before dispatch, cleared on switch and on failure. */
  readonly mappingErrorsRequestedFor: string;

  /** When mapping errors last landed, for the staleness check on a page opened long after the switch. */
  readonly mappingErrorsLoadedAt?: number;

  /** Whose mapping errors mappingErrorsLoadedAt is the age of. Read the two through the module's selector. */
  readonly mappingErrorsLoadedFor: string;

  /** The resort whose mappings load last landed, rows or not. Cleared on a switch. */
  readonly mappingsLoadedFor: string;

  /** The resort whose product hydration errors last landed. Cleared on a switch. */
  readonly productHydrationErrorsLoadedFor: string;

  /** Product Mapping edits are unsaved or still saving, so the resort selector is disabled. Owned by the page. */
  readonly productMappingHasUnsavedEdits: boolean;

  /** When a Product Mapping save last landed. A Mapping Status response requested before then may hold older values. */
  readonly productMappingsSavedAt?: number;

  readonly resortMappingHasError: boolean;

  /**
   * Information for Resort mapping tool - product hydration errors
   */

  readonly productHydrationErrors: ProductHydrationErrors[];

  /**
   * Information on Pass usage by Guest Id
   * Containing current and previous historical details on each day the guest spent on the mountain.
   */
  readonly guestRedemptionHistories: ImmutableMap<EntitlementRedemptionHistory[]>;

  /**
   * Guests have passes which have physically printed cards. These cards are "media", and a guest's
   * media history is the history of their past & presently printed cards, including details like RFID,
   * Print Date, and Active/Inactive status. Each object in this array has `guestId`, so if you're interested
   * in a specific guest's history, filter on that guest's master guest ID.
   */
  readonly mediaHistories: HistoricalMedia[];

  /**
   * Cache of valid lift access locations per resort + entitlement type, keyed by `${resortAbbreviation}-${entitlementTypeId}`.
   * Used to show which locations a location-restricted pass is valid at. Cached globally so it's reused across guests.
   */
  readonly validLiftAccessLocations: ImmutableMap<LiftAccessValidLocationsEntry>;

  /**
   * Guests can share days via friends and family.
   */
  readonly entitlementShares: EntitlementShare[];

  /**
   * Agents can log comments associated with a guest with any subject they choose.
   * A comment history will include details like Agent, Subject, Print Date, and the Comment, itself.
   * if you are interested in the comment history of a certain guest, just filter on the master guest ID of the guest.
   * A comment history will include details like Agent, Subject, Print Date, and the Comment itself.
   * if you are interested in the comment history of a certain guest, just filter on that guest's master guest ID.
   */
  readonly commentHistories: GuestComment[];

  readonly importSuccesses: string[]; // Where string = guestID

  readonly passPrinting: {
    readonly pdfs: PassPdf[]; // Guest IDs -> PDF
    readonly printed: string[]; // Array of guest IDs that are known to have been printed (got a webcam on the printer)
  };

  /**
   * Guests info for guests associated with payment method
   */
  readonly paymentGuests?: PaymentGuest[];

  /**
   * Can only ever have one redeemed entitlements at a time in the current UI. If you close the modal in which you
   * did the redemption, you lose the opportunity to ever go back.
   */
  readonly redeemedEntitlement?: Redemption;

  /**
   * Map between the integral redemption ID and a description of that ID
   * To support a growing number of dynamic redemption types
   */
  readonly redemptionTypes?: RedemptionType[];

  readonly migrationError?: string;

  readonly users?: User[];

  readonly usernameAvailable?: boolean;

  readonly emailAvailable?: boolean;

  readonly suggestedUsername?: string;

  readonly guestHydrationErrorReport?: GuestHydrationErrorReportData;

  readonly guestHydrationErrorReportCount?: number;

  readonly weeklyVisitorDataReports?: WeeklyVisitorDataReport[];

  readonly faqs?: AppDocItem[];

  readonly releaseNotes?: AppDocItem[];

  readonly trainingDocuments?: TrainingDocument[];

  /**
   * The Snackbar we're currently displaying. Snackbars are mostly used to show the state of API calls that
   * don't otherwise have a representation in the UI.
   */
  readonly snackbars: Snackbar[];

  /**
   * The current modal to show (or IKON_MODAL.NONE) and its optional bundle of data what that are passed as props.
   * You can set this to any kind of modal, but if the ModalHost doesn't know how to render that modal, then nothing
   * will happen.
   */
  readonly currentModal: {
    readonly type: IKON_MODAL;
    readonly extras?: any;
  };

  readonly textToClipboard?: string;
  readonly clipboardMsg?: string;
  readonly lastActivity?: Date;

  readonly locale: Record<string, any>;

  readonly defaultDateFormat: string;
  readonly defaultTimeFormat: string;

  readonly scriptVersions: VersionResponse[];
  readonly userSearchHistory: UserSearchHistory[];
}


/**
 * The app's default (initial) state given no other information.
 */
export const DEFAULT_STATE = {
  auth: {
    isLoggedIn: false,
    isLoggingIn: false,
    resetPassword: false,
  },
  resortToolsConfig: DEFAULT_CONFIG,
  applianceVersion: '',
  breakerBox: {
    maxFailureCount: DEFAULT_CONFIG.apiCircuitBreaker.failureMax,
    coolDownTimeMs: DEFAULT_CONFIG.apiCircuitBreaker.coolDownPeriodMs,
    breakers: ImmutableMap.empty(),
  },
  usernameAvailable: true,
  guests: ImmutableMap.empty(),
  entitlementHotlists: ImmutableMap.empty(),
  validLiftAccessLocations: ImmutableMap.empty(),
  knownGuestRfidCodes: [],
  households: [],
  mediaHistories: [],
  entitlementShares: [],
  guestResortHistories: ImmutableMap.empty(),
  guestRedemptionHistories: ImmutableMap.empty(),
  importSuccesses: [],
  passPrinting: {
    pdfs: [],
    printed: [],
  },
  commentHistories: [],
  loading: ImmutableMap.empty(),
  error: ImmutableMap.empty(),
  snackbars: [],
  currentModal: {
    type: IKON_MODAL.NONE,
  },
  resortMappingHasError: false,
  resortMappingHistories: defaultResortMappingObject,
  mappingErrorsRequestedFor: '',
  mappingErrorsLoadedFor: '',
  mappingsLoadedFor: '',
  productHydrationErrorsLoadedFor: '',
  productMappingHasUnsavedEdits: false,
  productHydrationErrors: [],
  entitlementTypesMetadata: [],
  editableEffectiveDateRangeAccessRules: [],
  effectiveDateRangeHistory: [],
  waiverLookup: ImmutableMap.empty(),
  locale: en_us,
  defaultDateFormat: 'MM/dd/yyyy', 
  defaultTimeFormat: 'h:mm a',
  scriptVersions: [],
  userSearchHistory: [],
  sellingSeasons: [],
  fulfillmentSeasons: [],
} as unknown as IAppState;
